import React from "react"
import { graphql, Link } from "gatsby"
import Layout from "../../components/Layout"
import * as styles from "../../styles/industry.module.css"

const imageFor = (images, relativePath) =>
  images.nodes.find(image => image.relativePath === relativePath)

const TeckIndustry = ({ data }) => {
  const inner = imageFor(data.images, "teckIntro/inner.png")

  return (
    <Layout>
      <article className={styles.detail}>
        <Link className={styles.backLink} to="/industry">
          industry/
        </Link>

        <header className={styles.detailHeader}>
          <p>Teck Resources Limited</p>
          <h1>Student Researcher</h1>
          <span>Feb. 2024 - Jun. 2026</span>
        </header>

        {inner && (
          <img
            className={`${styles.heroImage} ${styles.containImage}`}
            src={inner.publicURL}
            alt="Teck industrial analytics workflow"
          />
        )}

        <section className={styles.detailGrid}>
          <div className={styles.detailText}>
            <h2>Industrial Data Analytics and Optimization with Teck Resources</h2>
            <p>
              From 2024 to 2026, I collaborated with process control engineers
              at Teck Resources on two industrial projects at the Trail
              metallurgical operations. The work focused on applying data
              analytics, machine learning, optimization, and process-control
              methods to real plant problems.
            </p>
            <p>
              My role involved understanding operational requirements,
              developing and migrating Python-based analytical tools to
              Databricks, validating them with historical plant data, and
              presenting results to Teck engineers for review.
            </p>

            <h2>KIVCET Furnace Feed Optimization</h2>
            <p className={styles.sectionDate}>January 2025 - June 2026</p>
            <p>
              The KIVCET furnace processes a blend of concentrates, recycled
              materials, and residues. Feed decisions must balance several
              competing objectives, including residue utilization, feed
              moisture, chemical composition, equipment availability, and
              operating limits.
            </p>
            <p>
              A key challenge is that residue composition varies over time and
              is not always measured frequently enough for real-time
              decision-making. To address this problem, I helped develop a
              Databricks-based decision-support workflow that combines:
            </p>
            <ul>
              <li>Plant and laboratory data</li>
              <li>Machine-learning soft sensors for estimating residue composition</li>
              <li>An uncertainty-aware optimization model</li>
              <li>Historical simulation and current-time recommendation workflows</li>
            </ul>
            <p>
              The optimizer recommends furnace-feed adjustments while accounting
              for moisture limits, chemistry targets, material availability,
              equipment status, and restrictions on how quickly feed settings
              can change. It also includes fallback strategies for situations
              where all operating targets cannot be satisfied simultaneously.
            </p>
            <p>
              In a one-month historical backtest, the optimized strategy
              increased the frequency with which feed element-ratio targets were
              satisfied by 63%. The simulation also indicated the potential to
              utilize several hundred additional tonnes of residue per month
              compared with manual operation.
            </p>

            <h2>MPC Model Health Monitoring</h2>
            <p className={styles.sectionDate}>February 2024 - January 2025</p>
            <p>
              Model predictive controllers rely on internal mathematical models
              to predict how a plant will respond to control actions. As
              equipment and operating conditions change, these models may
              gradually become less representative of actual plant behaviour.
            </p>
            <p>
              In this project, I worked with Teck engineers to migrate a
              model-plant mismatch monitoring toolbox from a local desktop
              application to Databricks. The workflow was applied to two
              industrial MPC systems.
            </p>
            <p>The migrated system:</p>
            <ul>
              <li>Processes historical and current plant data</li>
              <li>Compares MPC model predictions with observed process behaviour</li>
              <li>Combines five complementary model-health indicators</li>
              <li>Identifies potentially affected input-output relationships</li>
              <li>Integrates the results into automated daily reports</li>
            </ul>
            <p>
              The monitoring methods examine model quality from different
              perspectives, including prediction errors, input-residual
              correlation, frequency-response changes, and model-parameter
              behaviour. Combining these indicators provides engineers with more
              context than relying on a single model-health metric.
            </p>
            <p>
              I also automated the generation of interactive HTML reports that
              bring together operating KPIs, process trends, controller
              information, and model-health alerts for engineering review.
            </p>
          </div>
        </section>
      </article>
    </Layout>
  )
}

export default TeckIndustry

export const query = graphql`
  query TeckIndustryPage {
    images: allFile(
      filter: {
        sourceInstanceName: { eq: "industry" }
        extension: { in: ["jpg", "jpeg", "png", "webp"] }
      }
    ) {
      nodes {
        publicURL
        relativePath
      }
    }
  }
`
